<template>
  <div
    v-if="visible"
    class="music-card"
    :class="{ 'is-expanded': expanded, 'is-playing': playing }"
    :style="{ left: left + 'px', bottom: bottom + 'px' }"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <!-- 卡片头部：图标 + 歌名 + 控制按钮 -->
    <div class="mc-head">
      <span class="mc-disk" :class="{ 'is-spinning': playing }">
        <i class="mc-note">♪</i>
      </span>
      <div class="mc-title-wrap">
        <div class="mc-title">{{ currentSong ? currentSong.name : '暂无音乐' }}</div>
        <div class="mc-status">
          <span v-if="playing" class="mc-eq" aria-hidden="true"><i></i><i></i><i></i></span>
          {{ playing ? '正在播放' : (currentSong ? '已暂停' : '未加载') }}
        </div>
      </div>
      <span class="mc-btn" @mousedown.stop @click="togglePlay">
        <i :class="playing ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
      </span>
      <span class="mc-btn" @mousedown.stop @click="playNext" title="下一首">
        <i class="el-icon-right"></i>
      </span>
      <span class="mc-btn" @mousedown.stop @click="expanded = !expanded" :title="expanded ? '收起' : '展开'">
        <i :class="expanded ? 'el-icon-caret-bottom' : 'el-icon-caret-top'"></i>
      </span>
    </div>

    <!-- 展开区：进度条 / 音量 / 播放列表 -->
    <div v-show="expanded" class="mc-body">
      <canvas ref="mcCanvas" class="mc-canvas" width="276" height="52"></canvas>
      <div class="mc-progress">
        <span class="mc-time">{{ fmtTime(current) }}</span>
        <el-slider
          class="mc-slider"
          :value="current"
          :max="duration || 1"
          :show-tooltip="false"
          :disabled="!currentSong"
          @input="onProgressInput"
          @change="onProgressChange"
        ></el-slider>
        <span class="mc-time">{{ fmtTime(duration) }}</span>
      </div>
      <div class="mc-volume">
        <i class="el-icon-mute mc-mute" @click="toggleMute"></i>
        <el-slider
          class="mc-slider"
          :value="volume"
          :show-tooltip="false"
          @input="setVolume"
        ></el-slider>
        <span class="mc-vol-num">{{ volume }}%</span>
      </div>
      <div class="mc-list">
        <div
          v-for="(s, i) in songs"
          :key="s.name"
          class="mc-item"
          :class="{ active: i === currentIndex }"
          @click="playAt(i)"
        >
          <span class="mc-item-name">{{ s.name }}</span>
          <span v-if="i === currentIndex" class="mc-item-tag">
            <span v-if="playing" class="mc-item-eq" aria-hidden="true"><i></i><i></i><i></i></span>
            {{ playing ? '播放中' : '暂停' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bus from '../utils/bus'

// 自动收集 src/assets/Music 目录下的所有音频文件（webpack 构建时打包）
const ctx = require.context('../assets/Music', false, /\.(mp3|wav|flac|ogg|m4a|aac)$/i)
const songList = ctx.keys().map(k => ({
  name: k.replace(/^\.\//, '').replace(/\.[^.]+$/, ''),
  url: ctx(k)
}))

export default {
  name: 'MusicPlayer',
  data() {
    return {
      visible: false,
      songs: songList,
      currentIndex: 0,
      playing: false,
      current: 0,
      duration: 0,
      volume: 70,
      muted: false,
      expanded: false,
      left: 20,
      bottom: 24,
      dragging: false,
      analyser: null,
      freqData: null,
      startX: 0,
      startY: 0,
      origLeft: 0,
      origBottom: 0
    }
  },
  computed: {
    currentSong() {
      return this.songs[this.currentIndex] || null
    }
  },
  created() {
    this.initAudio()
    this.initVisualizer()
  },
  mounted() {
    this.refresh()
    bus.$on('music:user-changed', this.refresh)
    this.startVisualLoop()
  },
  beforeDestroy() {
    bus.$off('music:user-changed', this.refresh)
    this.stopVisualLoop()
    if (this.audio) {
      this.audio.pause()
      this.audio.src = ''
    }
    if (this.actx) {
      try {
        this.actx.close()
      } catch (e) {
        /* 忽略关闭异常 */
      }
    }
  },
  methods: {
    // 仅超级管理员（roleId === 0）显示
    isSuperAdmin() {
      try {
        const cu = sessionStorage.getItem('CurUser')
        if (!cu) return false
        const u = JSON.parse(cu)
        return Number(u.roleId) === 0
      } catch (e) {
        console.warn('读取登录状态失败', e)
        return false
      }
    },
    refresh() {
      this.visible = this.isSuperAdmin()
      if (!this.visible) {
        this.pause()
      }
    },
    initAudio() {
      if (!this.currentSong) return
      this.audio = new Audio(this.currentSong.url)
      this.audio.preload = 'metadata'
      this.audio.volume = this.volume / 100
      this.audio.addEventListener('timeupdate', () => {
        this.current = this.audio.currentTime
      })
      this.audio.addEventListener('loadedmetadata', () => {
        this.duration = this.audio.duration || 0
      })
      this.audio.addEventListener('play', () => {
        this.playing = true
      })
      this.audio.addEventListener('pause', () => {
        this.playing = false
      })
      this.audio.addEventListener('ended', () => {
        this.playNext()
      })
      this.audio.addEventListener('error', () => {
        this.$message.error('音乐加载失败：' + (this.currentSong ? this.currentSong.name : '未知音乐'))
      })
    },
    // 动感音律：Web Audio 频谱分析
    initVisualizer() {
      if (!this.audio) return
      const AC = window.AudioContext || window.webkitAudioContext
      if (!AC) return
      if (!this.actx) {
        this.actx = new AC()
      }
      // 同一 audio 元素只能绑定一次 MediaElementSource
      if (!this._mediaSrc) {
        this._mediaSrc = this.actx.createMediaElementSource(this.audio)
        this.analyser = this.actx.createAnalyser()
        this.analyser.fftSize = 256
        this.analyser.smoothingTimeConstant = 0.8
        this._mediaSrc.connect(this.analyser)
        this.analyser.connect(this.actx.destination)
      }
      this.freqData = new Uint8Array(this.analyser.frequencyBinCount)
    },
    startVisualLoop() {
      if (this._raf) return
      const tick = () => {
        this._raf = requestAnimationFrame(tick)
        this.updateLevel()
        this.drawSpectrum()
      }
      this._raf = requestAnimationFrame(tick)
    },
    stopVisualLoop() {
      if (this._raf) {
        cancelAnimationFrame(this._raf)
        this._raf = null
      }
    },
    // 音律四射：将实时音量写入 CSS 变量，驱动彩色光晕扩散
    updateLevel() {
      const el = this.$el
      // 根元素可能是 v-if 占位注释节点（卡片隐藏时），注释节点没有 style，需跳过
      if (!el || el.nodeType !== 1 || !this.analyser || !this.freqData) return
      this.analyser.getByteFrequencyData(this.freqData)
      let sum = 0
      for (let i = 2; i < 48; i++) {
        sum += this.freqData[i]
      }
      const raw = sum / 46 / 255
      const lvl = this._level === undefined ? raw : this._level * 0.6 + raw * 0.4
      this._level = lvl
      el.style.setProperty('--mc-level', lvl.toFixed(3))
    },
    drawSpectrum() {
      const canvas = this.$refs.mcCanvas
      if (!canvas || canvas.clientWidth === 0) return
      const c = canvas.getContext('2d')
      const w = canvas.width
      const h = canvas.height
      c.clearRect(0, 0, w, h)
      if (!this.analyser || !this.freqData) return
      const bars = 30
      const gap = 2
      const bw = (w - gap * (bars - 1)) / bars
      const step = Math.floor((this.freqData.length - 2) / bars)
      for (let i = 0; i < bars; i++) {
        const v = this.freqData[2 + i * step] / 255
        const bh = Math.max(2, v * h * 0.92)
        const g = c.createLinearGradient(0, h, 0, h - bh)
        g.addColorStop(0, '#ffb6d9')
        g.addColorStop(1, '#e7549c')
        c.fillStyle = g
        c.fillRect(i * (bw + gap), h - bh, bw, bh)
      }
    },
    togglePlay() {
      if (!this.audio || !this.currentSong) return
      if (this.audio.paused) {
        if (this.actx && this.actx.state === 'suspended') {
          this.actx.resume().catch(() => {})
        }
        this.audio.play().catch(() => {
          this.$message.warning('播放被浏览器拦截，请手动点击一次')
        })
      } else {
        this.audio.pause()
      }
    },
    playNext() {
      if (!this.songs.length) return
      this.currentIndex = (this.currentIndex + 1) % this.songs.length
      this.switchTo(this.currentIndex)
    },
    playAt(i) {
      if (i < 0 || i >= this.songs.length) return
      this.currentIndex = i
      this.switchTo(i)
    },
    switchTo(i) {
      const song = this.songs[i]
      if (!song || !this.audio) return
      this.audio.src = song.url
      this.duration = 0
      this.current = 0
      if (this.actx && this.actx.state === 'suspended') {
        this.actx.resume().catch(() => {})
      }
      this.audio.play().catch(() => {
        this.$message.warning('播放被浏览器拦截，请手动点击一次')
      })
    },
    pause() {
      if (this.audio) {
        this.audio.pause()
      }
    },
    onProgressInput(v) {
      this.current = v
    },
    onProgressChange(v) {
      this.seekTo(v)
    },
    seekTo(v) {
      if (!this.audio || !this.duration) return
      this.audio.currentTime = v
      this.current = v
    },
    setVolume(v) {
      this.volume = v
      if (this.audio) {
        this.audio.volume = v / 100
        this.audio.muted = false
        this.muted = false
      }
    },
    toggleMute() {
      if (!this.audio) return
      this.muted = !this.muted
      this.audio.muted = this.muted
    },
    fmtTime(t) {
      if (!t || isNaN(t)) return '00:00'
      const m = Math.floor(t / 60)
      const s = Math.floor(t % 60)
      return (m < 10 ? '0' + m : '' + m) + ':' + (s < 10 ? '0' + s : '' + s)
    },
    startDrag(e) {
      const t = e.target
      if (!t || !t.closest) return
      if (t.closest('.mc-btn') || t.closest('.el-slider') || t.closest('.mc-list') || t.closest('.mc-volume')) {
        return
      }
      this.dragging = true
      const point = e.touches ? e.touches[0] : e
      this.startX = point.clientX
      this.startY = point.clientY
      this.origLeft = this.left
      this.origBottom = this.bottom
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.endDrag)
      document.addEventListener('touchmove', this.onDrag, { passive: false })
      document.addEventListener('touchend', this.endDrag)
    },
    onDrag(e) {
      if (!this.dragging) return
      e.preventDefault()
      const point = e.touches ? e.touches[0] : e
      const dx = point.clientX - this.startX
      const dy = point.clientY - this.startY
      const w = this.expanded ? 300 : 250
      const h = this.expanded ? 270 : 46
      let nl = this.origLeft + dx
      let nb = this.origBottom - dy
      nl = Math.max(0, Math.min(nl, window.innerWidth - w - 8))
      nb = Math.max(0, Math.min(nb, window.innerHeight - h - 8))
      this.left = nl
      this.bottom = nb
    },
    endDrag() {
      this.dragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.endDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.endDrag)
    }
  }
}
</script>

<style scoped>
.music-card {
  position: fixed;
  z-index: 3000;
  width: 250px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 24px rgba(201, 87, 139, 0.35);
  border: 1px solid rgba(238, 137, 183, 0.4);
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  font-family: inherit;
}
.music-card.is-expanded {
  width: 300px;
}
.mc-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  cursor: move;
  background: linear-gradient(135deg, #ffe3f0, #fff0f7);
}
.mc-disk {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff9ecb, #c9578b);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(201, 87, 139, 0.4);
}
.mc-note {
  font-size: 14px;
  font-style: normal;
}
.mc-title-wrap {
  flex: 1;
  min-width: 0;
}
.mc-title {
  font-size: 12px;
  color: #a13a6e;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mc-status {
  font-size: 10px;
  color: #d98bb4;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 4px;
}
.mc-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #c9578b;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(201, 87, 139, 0.3);
  flex-shrink: 0;
  transition: background 0.2s;
}
.mc-btn:hover {
  background: #ffd9ea;
}
.mc-body {
  padding: 4px 12px 12px;
}
.mc-progress {
  display: flex;
  align-items: center;
  gap: 6px;
}
.mc-time {
  font-size: 10px;
  color: #b0648c;
  min-width: 34px;
  text-align: center;
}
.mc-slider {
  flex: 1;
  padding: 0 4px;
}
.mc-volume {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  color: #c9578b;
}
.mc-mute {
  cursor: pointer;
  font-size: 14px;
}
.mc-vol-num {
  font-size: 10px;
  color: #b0648c;
  min-width: 30px;
  text-align: right;
}
.mc-list {
  max-height: 120px;
  overflow-y: auto;
  margin-top: 6px;
  border-top: 1px dashed #f3c6dd;
  padding-top: 6px;
}
.mc-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #a04a75;
}
.mc-item:hover {
  background: #ffe9f4;
}
.mc-item.active {
  background: #ffd9ea;
  color: #c9578b;
  font-weight: bold;
}
.mc-item-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mc-item-tag {
  font-size: 10px;
  color: #e07aab;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
::v-deep .mc-slider .el-slider__runway {
  height: 4px;
  margin: 0;
}
::v-deep .mc-slider .el-slider__bar {
  background: linear-gradient(90deg, #ff9ecb, #c9578b);
  height: 4px;
}
::v-deep .mc-slider .el-slider__button {
  margin-top: 10px;
  width: 8px;
  height: 8px;
  border: 2px solid #c9578b;
}
::v-deep .mc-slider .el-slider__button-wrapper {
  top: -16px;
  height: 24px;
}
::v-deep .mc-slider .el-slider__stop {
  display: none;
}

/* ===== 动感音律：唱片旋转（播放时） ===== */
.mc-disk.is-spinning {
  animation: mc-disk-spin 4s linear infinite;
}
@keyframes mc-disk-spin {
  to { transform: rotate(360deg); }
}

/* ===== 动感音律：头部迷你均衡器（播放时） ===== */
.mc-eq {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 11px;
}
.mc-eq i {
  width: 3px;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(180deg, #ff9ecb, #c9578b);
  animation: mc-eq-bounce 0.9s ease-in-out infinite;
}
.mc-eq i:nth-child(2) {
  animation-delay: 0.25s;
}
.mc-eq i:nth-child(3) {
  animation-delay: 0.5s;
}
@keyframes mc-eq-bounce {
  0%, 100% { height: 3px; }
  50% { height: 11px; }
}

/* ===== 动感音律：播放列表当前项的律动点 ===== */
.mc-item-eq {
  display: inline-flex;
  align-items: flex-end;
  gap: 1.5px;
  height: 9px;
}
.mc-item-eq i {
  width: 2px;
  height: 3px;
  border-radius: 1px;
  background: #e07aab;
  animation: mc-eq-bounce 0.8s ease-in-out infinite;
}
.mc-item-eq i:nth-child(2) {
  animation-delay: 0.2s;
}
.mc-item-eq i:nth-child(3) {
  animation-delay: 0.4s;
}

/* ===== 动感音律：展开区实时频谱画布 ===== */
.mc-canvas {
  display: block;
  width: 100%;
  height: 52px;
  margin: 2px 0 6px;
  border-radius: 8px;
  background: linear-gradient(180deg, #fff0f7, #ffe3f0);
}

/* ===== 动态效果：播放时音律四射彩色光晕（随频谱扩散） ===== */
.music-card {
  --mc-level: 0;
}
.music-card.is-playing {
  box-shadow:
    0 0 calc(10px + var(--mc-level) * 34px) rgba(255, 158, 203, 0.55),
    0 0 calc(26px + var(--mc-level) * 58px) rgba(177, 140, 255, 0.42),
    0 0 calc(44px + var(--mc-level) * 88px) rgba(126, 200, 255, 0.34),
    0 0 calc(64px + var(--mc-level) * 120px) rgba(127, 240, 216, 0.24),
    0 0 calc(84px + var(--mc-level) * 150px) rgba(255, 210, 127, 0.18),
    0 0 5px rgba(255, 130, 190, 0.65);
  animation: mc-hue-drift 12s ease-in-out infinite;
}
@keyframes mc-hue-drift {
  0%, 100% { filter: saturate(1.08) hue-rotate(0deg); }
  50% { filter: saturate(1.16) hue-rotate(16deg); }
}

/* ===== 动态效果：进度条流光（播放时） ===== */
.music-card.is-playing ::v-deep .mc-slider .el-slider__bar {
  background: linear-gradient(90deg, #ff9ecb, #c9578b, #ffb6d9, #c9578b);
  background-size: 200% 100%;
  animation: mc-bar-flow 2.8s linear infinite;
}
@keyframes mc-bar-flow {
  to { background-position: -200% 0; }
}
</style>
